import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import AccessRequests from './AccessRequests.jsx';
import { formatDate, initials } from '../../utils/format';
import Tag from '../shared/Tag';
import { useToast } from '../ui/Toast';

/** Return a human-readable "X hours ago" / "X days ago" string from a datetime string */
function timeAgo(dtStr) {
  if (!dtStr) return null;
  const diff = Date.now() - new Date(dtStr.replace(' ', 'T') + (dtStr.includes('T') ? '' : 'Z')).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 2)   return 'just now';
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days  < 7)   return `${days}d ago`;
  if (days  < 30)  return `${Math.floor(days / 7)}w ago`;
  if (days  < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
import Modal from '../shared/Modal';
import { useApp } from '../../context';
import { parseRoles, canAccess } from '../../utils/rbac';
import PersonLaptopTab from './PersonLaptopTab';
import { allowanceLeft, approvedLeaveDays } from '../../utils/leaveDays';
import { personToSelect, shouldClearPerson } from '../../utils/peopleDeepLink';

// Every dropdown on the platform reads in alphabetical order (El's item 35), so
// the sort lives next to the lists rather than being remembered at each use.
const sortByLabel = (list) =>
  [...(Array.isArray(list) ? list : [])].sort((a, b) =>
    String(a.label || a.value || '').localeCompare(String(b.label || b.value || ''), 'en-GB', { sensitivity: 'base' }));

// Keep in sync with the role keys in frontend/src/utils/rbac.js (PERMISSIONS).
// 'contractor' is intentionally omitted — contractors are managed as resource
// workers, not assigned this role from the People page.
const ROLES = [
  { value: 'admin',             label: 'Admin — full access' },
  { value: 'partner',           label: 'Partner' },
  { value: 'commercial-lead',   label: 'Commercial Lead' },
  { value: 'pm',                label: 'Project Manager' },
  { value: 'pmo',               label: 'PMO — milestone management' },
  { value: 'bd',                label: 'Business Development' },
  { value: 'finance',           label: 'Finance' },
  { value: 'billing',           label: 'Billing' },
  { value: 'hr',                label: 'HR' },
  { value: 'middle_manager',    label: 'Middle Manager (names the group in the People & Compliance Plan, opens nothing)' },
  { value: 'inventory-manager', label: 'Inventory Manager' },
  { value: 'qms_manager',       label: 'QMS Manager' },
  { value: 'engineer',          label: 'Engineer' },
  { value: 'client',            label: 'Client' },
];

const CLEARANCE_LEVELS = ['none','bpss','sc','esc','dv','edv'];
const EMPLOYMENT_TYPES  = ['permanent','contractor','fixed_term'];
const RATE_BANDS        = ['B1','B2','B3','B4','B5','B6','SP2','SP3'];

const EMPLOYMENT_TYPE_LABELS = {
  permanent:  'Permanent',
  contractor: 'Contractor',
  fixed_term: 'Fixed Term',
};

const DOC_TYPES = [
  { value: 'id',            label: 'ID Document' },
  { value: 'reference',     label: 'Reference' },
  { value: 'p60',           label: 'P60' },
  { value: 'enquiry',       label: 'Enquiry' },
  { value: 'complaint',     label: 'Complaint' },
  { value: 'breach',        label: 'Breach' },
  { value: 'investigation', label: 'Investigation' },
  { value: 'other',         label: 'Other' },
];

function M365PersonTab({ personId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [linkUpn, setLinkUpn] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await api.get(`m365/${personId}`);
      setData(result);
    } catch (e) {
      setError(e.message || 'Failed to fetch M365 data');
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => { load(); }, [load]);

  const handleLink = async (e) => {
    e.preventDefault();
    if (!linkUpn.trim()) return;
    setLinking(true); setLinkError(null);
    try {
      await api.post(`m365/${personId}/link`, { upn: linkUpn.trim() });
      setLinkUpn('');
      await load();
    } catch (e) {
      setLinkError(e.message || 'User not found in Entra ID');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!confirm('Remove the M365 link from this person record? This does not affect the Entra account itself.')) return;
    await api.delete(`m365/${personId}/link`).catch(() => {});
    await load();
  };

  if (loading) return <div className="text-[9px] text-gray-400 py-4 text-center">Loading M365 data…</div>;
  if (error)   return <div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>;

  if (data?.notProvisioned) return (
    <div className="space-y-3">
      <div className="text-center py-4">
        <div className="text-2xl mb-1">☁</div>
        <div className="text-[10px] font-semibold text-gray-600">No M365 account linked</div>
        <div className="text-[9px] text-gray-400 mt-0.5">Link an existing Entra account, or provision one via onboarding.</div>
      </div>
      <div className="border border-gray-200 rounded-lg p-3 space-y-2">
        <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Link Existing M365 Account</div>
        <form onSubmit={handleLink} className="flex gap-2">
          <input
            type="text"
            value={linkUpn}
            onChange={e => setLinkUpn(e.target.value)}
            placeholder="user@altonvalley.com"
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-[9px] font-mono focus:outline-none focus:border-gold"
          />
          <button
            type="submit"
            disabled={linking || !linkUpn.trim()}
            className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50"
          >
            {linking ? 'Looking up…' : 'Link'}
          </button>
        </form>
        {linkError && <div className="text-[9px] text-red-600">{linkError}</div>}
        <div className="text-[8px] text-gray-400">Enter the user's UPN (email address) as it appears in Entra ID / M365 Admin.</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Account summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Entra ID Account</span>
          <div className="flex items-center gap-2">
            <span className={`text-[8px] px-2 py-0.5 rounded-full font-semibold ${data.accountEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {data.accountEnabled ? 'Active' : 'Disabled'}
            </span>
            <button onClick={handleUnlink} className="text-[8px] text-gray-400 hover:text-red-500" title="Remove link">✕ Unlink</button>
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="text-[9px] text-gray-500">UPN</span>
            <span className="text-[9px] font-mono text-gray-800">{data.userPrincipalName || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[9px] text-gray-500">Display name</span>
            <span className="text-[9px] text-gray-800">{data.displayName || '—'}</span>
          </div>
          {data.lastPasswordChangeDateTime && (
            <div className="flex justify-between">
              <span className="text-[9px] text-gray-500">Password changed</span>
              <span className="text-[9px] text-gray-800">{new Date(data.lastPasswordChangeDateTime).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Licenses */}
      <div>
        <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Assigned Licenses</div>
        {data.licenses.length === 0 ? (
          <div className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            No licenses assigned. Visit the M365 Admin Portal to assign a license.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.licenses.map(l => (
              <span key={l.skuId} className="text-[9px] px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-full font-medium">
                {l.displayName}
              </span>
            ))}
          </div>
        )}
      </div>

      <button onClick={load} className="text-[8px] text-gray-400 hover:text-gray-600">↻ Refresh</button>
    </div>
  );
}

function AiUsagePersonTab({ personId }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth]     = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    setLoading(true);
    api.get('ai-usage', { person_id: personId, month }).then(d => { setRows(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [personId, month]);

  const totalCost = rows.reduce((s, r) => s + (r.cost_usd || 0), 0);
  const totalIn   = rows.reduce((s, r) => s + (r.input_tokens || 0), 0);
  const totalOut  = rows.reduce((s, r) => s + (r.output_tokens || 0), 0);

  const FEATURE_LABELS = {
    'sow-ai-plan':     'SOW AI Plan',
    'generate-sow-doc':'SOW Doc Gen',
    'ai-doc-diff':     'Doc Compare',
    'ai-redact':       'Redact SOW',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wide">Month</div>
        <input type="month" className="form-input w-36 text-[9px]" value={month} onChange={e => setMonth(e.target.value)} />
      </div>

      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-2 text-center">
            <div className="text-[7px] uppercase text-purple-400 font-bold">AI Calls</div>
            <div className="text-[14px] font-extrabold text-purple-700">{rows.length}</div>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-2 text-center">
            <div className="text-[7px] uppercase text-blue-400 font-bold">Tokens Used</div>
            <div className="text-[14px] font-extrabold text-blue-700">{(totalIn + totalOut).toLocaleString()}</div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 text-center">
            <div className="text-[7px] uppercase text-amber-500 font-bold">Est. Cost</div>
            <div className="text-[14px] font-extrabold text-amber-700">${totalCost.toFixed(4)}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4 text-[9px] text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-[9px] text-gray-400">No AI usage recorded for this month.</div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-[8px]">
          <thead><tr className="bg-gray-50 border-b">
            {['Feature','Entity','Tokens In','Tokens Out','Cost (USD)','Date'].map(h => (
              <th key={h} className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-2 py-1">
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold text-[7px]">{FEATURE_LABELS[r.feature] || r.feature}</span>
                </td>
                <td className="px-2 py-1 text-gray-500">{r.entity_type ? `${r.entity_type}` : '—'}</td>
                <td className="px-2 py-1 text-right font-mono">{(r.input_tokens || 0).toLocaleString()}</td>
                <td className="px-2 py-1 text-right font-mono">{(r.output_tokens || 0).toLocaleString()}</td>
                <td className="px-2 py-1 text-right font-mono text-amber-700">${(r.cost_usd || 0).toFixed(4)}</td>
                <td className="px-2 py-1 text-gray-400">{r.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t">
            <tr>
              <td colSpan={2} className="px-2 py-1 font-bold text-[7px] uppercase text-gray-500">Total</td>
              <td className="px-2 py-1 text-right font-bold font-mono">{totalIn.toLocaleString()}</td>
              <td className="px-2 py-1 text-right font-bold font-mono">{totalOut.toLocaleString()}</td>
              <td className="px-2 py-1 text-right font-bold font-mono text-amber-700">${totalCost.toFixed(4)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
        </div>
      )}
    </div>
  );
}

// Per-person panel to issue / revoke the personal access code an employee uses
// to connect the company Claude (MCP) connector. The plaintext code is shown
// exactly once, right after generation — it is never stored or retrievable.
const MCP_CONNECTOR_URL = 'https://ops.altonvalley.com/mcp';
function McpAccessPanel({ personId }) {
  const { toast } = useToast();
  const [status, setStatus]   = useState(null);   // { has_code, code_hint, created_at, last_used_at }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [newCode, setNewCode] = useState(null);    // plaintext, shown once
  const [emailedTo, setEmailedTo] = useState(null); // work email the code was emailed to, if any

  const load = useCallback(() => {
    setLoading(true);
    api.get(`people/${personId}/mcp-code`).then(d => { setStatus(d); setLoading(false); }).catch(() => setLoading(false));
  }, [personId]);
  useEffect(() => { setNewCode(null); setEmailedTo(null); load(); }, [personId, load]);

  const generate = async () => {
    if (status?.has_code && !window.confirm('This person already has an active code. Generating a new one immediately stops their current Claude connection until they reconnect with the new code. Continue?')) return;
    setBusy(true);
    try {
      const d = await api.post(`people/${personId}/mcp-code`);
      setNewCode(d.code);
      setEmailedTo(d.emailed ? d.email : null);
      await load();
    }
    catch (e) { toast.error('Could not generate code: ' + e.message); }
    setBusy(false);
  };
  const revoke = async () => {
    if (!window.confirm('Revoke this person’s Ops access code? Their Claude connection will stop working immediately.')) return;
    setBusy(true);
    try { await api.delete(`people/${personId}/mcp-code`); setNewCode(null); await load(); }
    catch (e) { toast.error('Could not revoke: ' + e.message); }
    setBusy(false);
  };
  const copy = (txt) => navigator.clipboard?.writeText(txt).then(() => toast.success('Copied to clipboard')).catch(() => {});

  return (
    <div className="mt-5 pt-4 border-t border-gray-200">
      <h4 className="text-[9px] font-bold mb-2 text-gray-600 uppercase tracking-wide">Claude / Ops MCP Access</h4>
      <p className="text-[9px] text-gray-500 mb-3 leading-relaxed">
        Lets this person connect Claude to Ops. It acts as them: it reads what they can see, and makes only the changes their own access allows (comments, Pipeline Tracker bids, QMS records), all recorded under their name.
        They add the connector once, then enter their personal access code to approve. Revoke any time to cut access instantly.
      </p>

      {loading ? (
        <div className="text-[9px] text-gray-400">Loading…</div>
      ) : (
        <>
          {status?.has_code ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-[9px] text-emerald-800 mb-2">
              <div className="font-semibold">Active code issued <span className="font-mono">…{status.code_hint}</span></div>
              <div className="text-emerald-600 mt-0.5">
                Created {formatDate(status.created_at)}
                {status.last_used_at ? ` · last used ${timeAgo(status.last_used_at)}` : ' · not yet used'}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded p-2.5 text-[9px] text-gray-500 mb-2">
              No access code — this person can’t connect Claude to Ops yet.
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={generate} disabled={busy}
              className="px-3 py-1.5 text-[9px] font-semibold rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50">
              {status?.has_code ? 'Regenerate code' : 'Generate code'}
            </button>
            {status?.has_code && (
              <button onClick={revoke} disabled={busy}
                className="px-3 py-1.5 text-[9px] font-semibold rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
                Revoke
              </button>
            )}
          </div>

          {newCode && (
            <div className="mt-3 bg-amber-50 border border-amber-300 rounded p-3">
              <div className="text-[9px] font-bold text-amber-800 mb-1">New access code — copy it now, it won’t be shown again</div>
              <div className="text-[8px] mb-1.5">
                {emailedTo
                  ? <span className="text-emerald-700">✓ Emailed to {emailedTo} with setup instructions.</span>
                  : <span className="text-amber-700">Not emailed (no work email on file, or email is currently off) — send the code and steps below manually.</span>}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <code className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5 font-mono text-[12px] tracking-wider text-navy select-all">{newCode}</code>
                <button onClick={() => copy(newCode)} className="px-2 py-1.5 text-[9px] font-semibold rounded bg-amber-600 text-white hover:bg-amber-700">Copy</button>
              </div>
              <div className="text-[8px] text-amber-700 leading-relaxed">
                <div className="font-semibold mb-0.5">Send these to the employee:</div>
                <div>1. In Claude → Settings → Connectors → add the Ops connector:</div>
                <div className="flex items-center gap-1.5 my-1">
                  <code className="bg-white border border-amber-200 rounded px-1.5 py-1 font-mono text-[8px]">{MCP_CONNECTOR_URL}</code>
                  <button onClick={() => copy(MCP_CONNECTOR_URL)} className="px-1.5 py-1 text-[8px] font-semibold rounded bg-amber-600 text-white hover:bg-amber-700">Copy URL</button>
                </div>
                <div>2. Click Connect → when prompted, enter the access code above to approve.</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// End every Ops session somebody has, on every device (pen test AD26341, AD8
// "Session Management"). For a lost or stolen laptop or phone, or an account
// thought to be in somebody else's hands. They can sign in again afterwards;
// what stops is any session that already existed. The confirmation and the
// result stay in the panel, beside the button.
function EndSessionsPanel({ person }) {
  const [state, setState] = useState({ step: 'idle' }); // idle | confirm | busy | done | error
  useEffect(() => { setState({ step: 'idle' }); }, [person.id]);
  const name = `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'this person';

  const end = async () => {
    setState({ step: 'busy' });
    try {
      const r = await api.post(`people/${person.id}/end-sessions`, {});
      setState({ step: 'done', at: r?.revoked_at ? new Date(r.revoked_at) : new Date() });
    } catch (e) {
      setState({ step: 'error', message: e.message || 'The sessions could not be ended. Try again.' });
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-gray-200">
      <h4 className="text-[9px] font-bold mb-2 text-gray-600 uppercase tracking-wide">Signed-in sessions</h4>
      <p className="text-[9px] text-gray-500 mb-3 leading-relaxed">
        Ends every Ops session {name} has, on every computer, phone and browser. Use it for a lost or stolen
        device, or an account you think somebody else is using. They can still sign in again afterwards.
      </p>
      {state.step === 'confirm' || state.step === 'busy' ? (
        <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[9px] text-amber-800">
          <div className="font-semibold mb-2">End all of {name}'s sessions now? Anything they have open in Ops stops working until they sign in again.</div>
          <div className="flex gap-2">
            <button onClick={end} disabled={state.step === 'busy'}
              className="px-3 py-1.5 text-[9px] font-semibold rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50">
              {state.step === 'busy' ? 'Ending…' : 'End sessions'}
            </button>
            <button onClick={() => setState({ step: 'idle' })} disabled={state.step === 'busy'}
              className="px-3 py-1.5 text-[9px] font-semibold rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Leave them signed in
            </button>
          </div>
        </div>
      ) : (
        <>
          <button onClick={() => setState({ step: 'confirm' })}
            className="px-3 py-1.5 text-[9px] font-semibold rounded border border-red-300 text-red-600 hover:bg-red-50">
            End sessions
          </button>
          {state.step === 'done' && (
            <div role="status" className="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2.5 text-[9px] text-emerald-800">
              Every session {name} had was ended at {state.at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.
              Their devices will be asked to sign in again. This is recorded in the audit trail.
            </div>
          )}
          {state.step === 'error' && (
            <div role="alert" className="mt-2 bg-red-50 border border-red-200 rounded p-2.5 text-[9px] text-red-700">{state.message}</div>
          )}
        </>
      )}
    </div>
  );
}

export default function PeoplePage({ initialPersonId = null }) {
  const { currentUser, navigate } = useApp();
  const { toast } = useToast();
  // ── BEING ON THIS SCREEN IS THE PERMISSION ─────────────────────────────────
  //
  // This used to read `['admin', 'hr']`, which meant an administrator could grant
  // the People page to a role in Admin → Roles & Permissions and the screen would
  // still treat that role as a stranger: no tabs, no edit, and a detail-changes
  // call it refused to make. The grant is the decision, so the question the page
  // asks is whether the page was granted, not what the role is called.
  //
  // Changing somebody's role is deliberately not covered by it. That is the one
  // action that can widen the actor's own access, so it stays with
  // administrators (isRealAdmin, below), exactly as the API does.
  const canManagePeople = canAccess('people');
  // Letting somebody into the app at all follows its own page, so an
  // administrator can grant it in Roles & Permissions without a deploy and the
  // server reads the same grant. Deliberately separate from canManagePeople:
  // several roles can read the People page.
  const canApproveAccess = canAccess('access-requests');
  // The Laptop tab follows the Laptops page's own grant, not the People page's:
  // it reads the same data, so the same administrator decision opens both.
  const canSeeLaptops = canAccess('laptops');
  const [people, setPeople] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [teams, setTeams]   = useState([]);
  const [jobTitles, setJobTitles]   = useState([]);
  const [companyLeaveDays, setCompanyLeaveDays] = useState(null);
  const [grades, setGrades]         = useState([]);
  const [clearances, setClearances] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]     = useState('');
  const [tab, setTab]           = useState('details');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit]     = useState(false);
  const [photoModal, setPhotoModal] = useState(false);
  const [leaveAllowances, setLeaveAllowances] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [roleChanging, setRoleChanging] = useState(false);
  const [personDocs, setPersonDocs] = useState([]);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docForm, setDocForm] = useState({ doc_type: 'id', title: '', url: '', notes: '' });
  const [empFilter, setEmpFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const [onboardingMap, setOnboardingMap] = useState({}); // person_id → 'onboarding' | 'offboarding'
  // Item 21: what people have changed about themselves and HR has not looked at
  // yet. The change is already saved — this is the list, not a queue of approvals.
  const [detailChanges, setDetailChanges] = useState([]);
  const loadDetailChanges = () => {
    if (!canManagePeople) return;
    api.get('people/detail-changes').then(r => setDetailChanges(Array.isArray(r) ? r : [])).catch(() => {});
  };
  useEffect(loadDetailChanges, [canManagePeople]);
  const acknowledgeChanges = async (personId) => {
    await api.put('people/detail-changes', { person_id: personId }).catch(() => {});
    loadDetailChanges();
  };

  // Job titles, grades and clearance levels are admin-managed lists rather than
  // hardcoded selects, so adding one is a change in Admin → Settings and not a
  // deploy. Sorted alphabetically here, as every list on the platform now is.
  const load = () => {
    setLoadError(null);
    Promise.all([
      api.get('people', { status: 'all' }),
      api.get('teams'),
      // ── ONE REFUSED EXTRA MUST NOT EMPTY THE SCREEN ──────────────────────
      //
      // Onboarding is its own page with its own permission, and this call only
      // decorates the list with a badge saying who is mid-onboarding. It used to
      // sit uncaught in this Promise.all, so for anybody holding People without
      // Onboarding — which is every role the page is granted to rather than
      // owning by default — its 403 rejected the whole batch. The list arrived
      // and was thrown away, and the screen said "No people found", which reads
      // as an empty company rather than a refused call.
      api.get('onboarding?status=in_progress').catch(() => []),
      api.get('config-options', { category: 'job_title' }).catch(() => []),
      api.get('config-options', { category: 'grade' }).catch(() => []),
      api.get('config-options', { category: 'security_clearance' }).catch(() => []),
      // The company's full-time entitlement. Read rather than assumed, so the form states
      // the figure that will actually be used instead of a number typed here.
      api.get('app-settings/annual_leave_days').catch(() => null),
    ]).then(([p, t, ob, jt, gr, sc, ald]) => {
      setPeople(p); setTeams(t);
      setCompanyLeaveDays(ald?.value != null && ald.value !== '' ? parseFloat(ald.value) : 23);
      setJobTitles(sortByLabel(jt));
      setGrades(Array.isArray(gr) ? gr : []);
      setClearances(Array.isArray(sc) ? sc : []);
      // Build a map of person_id → active process type
      const map = {};
      (ob || []).forEach(r => { map[r.person_id] = r.type; });
      setOnboardingMap(map);
    }).catch(err => {
      // A genuine failure says so. An empty list and a failed load used to render
      // identically, which is how a 403 on one decorative call passed for months
      // as "there are no people".
      setLoadError(err?.message || 'The people list could not be loaded.');
    });
  };
  useEffect(load, []);

  // Teams come back in insertion order; every list on the platform reads
  // alphabetically (item 35).
  const sortedTeams = [...teams].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), 'en-GB', { sensitivity: 'base' }));

  const filtered = people.filter(p => {
    const isArchived = p.employee_status === 'archived';
    if (!showArchived && isArchived) return false;
    if (showArchived && !isArchived) return false;
    if (!showArchived && empFilter !== 'all' && (p.employment_type || 'permanent') !== empFilter) return false;
    if (!search) return true;
    const fn = `${p.first_name} ${p.last_name}`.toLowerCase();
    return fn.includes(search.toLowerCase()) || (p.designation || '').toLowerCase().includes(search.toLowerCase());
  });

  const selectPerson = async (id) => {
    setSelected(id); setTab('details');
    try {
      const [la, lr, docs] = await Promise.all([
        api.get(`people/${id}/leave-allowances`, { year: '2026-27' }),
        api.get(`people/${id}/leave-requests`),
        api.get(`people/${id}/documents`),
      ]);
      setLeaveAllowances(la);
      setLeaveRequests(lr);
      setPersonDocs(docs);
    } catch { setLeaveAllowances([]); setLeaveRequests([]); setPersonDocs([]); }
  };

  // A click on this page moves the address with the selection, so #people/<id>
  // always names who is selected. Without that, opening the person already in
  // the address from the global search changes nothing and is ignored.
  const openPerson = (id) => { selectPerson(id); navigate('people', id); };
  const clearSelection = () => { setSelected(null); navigate('people', null); };

  // #people/<id> selects that person once the list has arrived, whether it came
  // from the global search, a refresh or another page's link. An address naming
  // nobody on the list lets the current person go, so the panel reads "Select a
  // person". The address is already right there, so this clears without
  // navigating. See peopleDeepLink.
  useEffect(() => {
    const id = personToSelect(initialPersonId, selected, people);
    if (id) selectPerson(id);
    else if (shouldClearPerson(initialPersonId, selected, people)) setSelected(null);
  }, [initialPersonId, people]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadAllowances = async (id) => {
    try {
      const [la, lr] = await Promise.all([
        api.get(`people/${id}/leave-allowances`, { year: '2026-27' }),
        api.get(`people/${id}/leave-requests`),
      ]);
      setLeaveAllowances(la);
      setLeaveRequests(lr);
    } catch { setLeaveAllowances([]); setLeaveRequests([]); }
  };

  const reloadDocs = async (id) => {
    try { setPersonDocs(await api.get(`people/${id}/documents`)); }
    catch { setPersonDocs([]); }
  };

  const addDoc = async () => {
    if (!selected || !docForm.title.trim() || !docForm.url.trim()) return;
    await api.post(`people/${selected}/documents`, { ...docForm });
    setDocForm({ doc_type: 'id', title: '', url: '', notes: '' });
    setShowAddDoc(false);
    reloadDocs(selected);
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Remove this document link?')) return;
    await api.delete(`people/${selected}/documents/${docId}`);
    reloadDocs(selected);
  };

  const person = people.find(p => p.id === selected);
  const manager = person ? people.find(p => p.id === person.reporting_manager_id) : null;
  // ── A CONTRACTOR HAS NO LEAVE AND NO BENEFITS ───────────────────────────────
  //
  // "All contractors still say Holiday Entitlement on work page - 25 days, and on Leave
  // tab - 25 days and benefits. None of this should be on there."
  //
  // They invoice for days worked. The edit form already refused to ask for an entitlement;
  // what was left was every read-only panel showing one anyway, and a Leave tab offering
  // allowances against somebody who accrues none.
  const isContractor = person?.employment_type === 'contractor';
  // The allowance actually calculated for the year — annual plus the long-service days,
  // which is the figure the person sees on their own screen. Shown on Work instead of the
  // typed baseline, so the two tabs cannot disagree.
  const annualAllowance = (() => {
    const rows = (leaveAllowances || []).filter(a => ['annual', 'tenure_annual'].includes(a.leave_type));
    if (!rows.length) return null;
    return Math.round(rows.reduce((n, a) => n + Number(a.total_days || 0), 0) * 2) / 2;
  })();
  const clearanceExpiring = person?.clearance_expiry && new Date(person.clearance_expiry) < new Date(Date.now() + 180 * 86400000);

  const create = async (data) => { await api.post('people', data); setShowCreate(false); load(); };

  const archivePerson = async () => {
    if (!person) return;
    const isArchived = person.employee_status === 'archived';
    if (!window.confirm(`${isArchived ? 'Restore' : 'Archive'} ${person.first_name} ${person.last_name}? ${isArchived ? 'They will become active again.' : 'They will be hidden from the main people list.'}`)) return;
    setArchiving(true);
    try {
      await api.put(`people/${person.id}`, { employee_status: isArchived ? 'active' : 'archived' });
      load();
      if (!isArchived) { clearSelection(); setShowArchived(false); }
    } catch (e) { toast.error('Failed: ' + e.message); }
    setArchiving(false);
  };

  const deletePerson = async () => {
    if (!person || person.employee_status !== 'archived') return;
    if (!window.confirm(`Permanently delete ${person.first_name} ${person.last_name}? This cannot be undone.`)) return;
    if (!window.confirm(`Are you sure? All data for ${person.first_name} ${person.last_name} will be permanently removed.`)) return;
    try {
      await api.delete(`people/${person.id}`);
      clearSelection();
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  const savePerson = async (data) => {
    await api.put(`people/${person.id}`, data);
    setShowEdit(false);
    load();
  };

  const toggleRole = async (personId, currentRoleStr, roleName) => {
    setRoleChanging(true);
    try {
      const current = currentRoleStr ? currentRoleStr.split(',').map(r => r.trim()).filter(Boolean) : [];
      const updated = current.includes(roleName)
        ? current.filter(r => r !== roleName)   // remove
        : [...current, roleName];               // add
      if (updated.length === 0) {
        toast.error('A person must have at least one role assigned.');
        setRoleChanging(false);
        return;
      }
      await api.put(`people/${personId}`, { user_role: updated.join(',') });
      load();
    } catch (e) {
      toast.error('Failed to update role: ' + e.message);
    }
    setRoleChanging(false);
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">People</h2>
          <div className="text-[9px] text-gray-500">{people.length} records</div>
        </div>
        {canManagePeople && (
          <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
            + Add Person
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input className="form-input max-w-[220px]" placeholder="Search name or title..." value={search} onChange={e => setSearch(e.target.value)} />
        {!showArchived && (
          <div className="flex gap-1">
            {[
              { key: 'all',        label: 'All' },
              { key: 'permanent',  label: 'Permanent' },
              { key: 'contractor', label: 'Contractor' },
              { key: 'fixed_term', label: 'Fixed Term' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setEmpFilter(f.key)}
                className={`px-2 py-0.5 text-[9px] font-semibold rounded border transition-all ${
                  empFilter === f.key
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-navy/40 hover:text-navy'
                }`}
              >{f.label}</button>
            ))}
          </div>
        )}
        <button
          onClick={() => { setShowArchived(v => !v); clearSelection(); }}
          className={`ml-auto px-2 py-0.5 text-[9px] font-semibold rounded border transition-all ${
            showArchived
              ? 'bg-amber-500 text-amber-950 border-amber-500'
              : 'bg-white text-gray-400 border-gray-200 hover:border-amber-400 hover:text-amber-600'
          }`}
        >📦 {showArchived ? 'Viewing Archived' : 'Show Archived'}</button>
      </div>

      {/* Item 21: what people have changed about themselves. Already saved — HR's
          job here is to check it against the file and tick it off, not to approve
          it. Grouped by person, because a house move is four fields and one
          event. */}
      <AccessRequests canApprove={canApproveAccess} onChanged={load} />

      {canManagePeople && detailChanges.length > 0 && (
        <div className="bg-white border border-amber-200 rounded mb-2 overflow-hidden">
          <div className="px-3 py-1.5 border-b border-amber-200 bg-amber-50 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[9px] font-bold text-amber-800">
              Details changed by employees ({detailChanges.length})
            </span>
            <span className="text-[8px] text-amber-700">Saved already. Check against the HR file, then tick off.</span>
          </div>
          {Object.values(detailChanges.reduce((acc, c) => {
            (acc[c.person_id] ||= { person_id: c.person_id, person_name: c.person_name, changes: [] }).changes.push(c);
            return acc;
          }, {})).map(group => (
            <div key={group.person_id} className="px-3 py-1.5 border-b border-gray-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <button onClick={() => openPerson(group.person_id)}
                  className="text-[10px] font-semibold text-navy hover:underline">{group.person_name}</button>
                <div className="text-[8px] text-gray-500 mt-0.5 space-y-0.5">
                  {group.changes.map(c => (
                    <div key={c.id}>
                      <span className="capitalize">{c.field.replace(/_/g, ' ')}</span>:{' '}
                      <span className="text-gray-400 line-through">{c.old_value || 'blank'}</span>{' → '}
                      <span className="text-gray-700 font-semibold">{c.new_value || 'blank'}</span>
                      <span className="text-gray-300 ml-1">{formatDate(c.changed_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => acknowledgeChanges(group.person_id)}
                className="px-2 py-0.5 text-[8px] font-semibold border border-gray-200 rounded text-gray-600 hover:bg-gray-50 whitespace-nowrap">
                ✓ Checked
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
        {/* List — compact */}
        <div className="bg-white border border-gray-200 rounded max-h-[calc(100vh-200px)] overflow-y-auto">
          {filtered.map(p => (
            <div
              key={p.id}
              className={`flex gap-2 px-2.5 py-1.5 border-b border-gray-100 cursor-pointer hover:bg-gold/5 ${selected === p.id ? 'bg-gold/10 border-l-2 border-l-gold' : ''}`}
              onClick={() => openPerson(p.id)}
            >
              {p.profile_photo ? (
                <img src={p.profile_photo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5" />
              ) : (
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[7px] font-bold text-white flex-shrink-0 mt-0.5" style={{ background: p.avatar_color || '#A1723D' }}>
                  {initials(`${p.first_name} ${p.last_name}`)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <div className="text-[9.5px] font-semibold truncate leading-tight">{p.first_name} {p.last_name}</div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    {p.employee_status === 'archived' && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1" title="Archived" />}
                    {onboardingMap[p.id] === 'onboarding' && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1" title="Onboarding" />}
                    {onboardingMap[p.id] === 'offboarding' && <span className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1" title="Offboarding" />}
                    {p.security_clearance && p.security_clearance !== 'none' && <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1" title={p.security_clearance.toUpperCase()} />}
                  </div>
                </div>
                <div className="text-[7.5px] text-gray-400 truncate leading-tight">{p.designation || 'No title'}</div>
              </div>
            </div>
          ))}
          {/* A failed load and an empty company are not the same thing, and the
              screen used to render them identically. */}
          {loadError && (
            <div className="m-2 p-2 rounded border border-red-200 bg-red-50 text-[9px] text-red-700 leading-relaxed">
              {loadError}
              <button onClick={load} className="ml-2 underline font-semibold">Try again</button>
            </div>
          )}
          {!loadError && filtered.length === 0 && <div className="text-center py-8 text-[10px] text-gray-400">No people found</div>}
        </div>

        {/* Detail */}
        <div>
          {person ? (
            <div className="bg-white border border-gray-200 rounded fade-in">
              {/* Header — redesigned */}
              <div className="rounded-t overflow-hidden">
                {/* Gold accent bar */}
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #C9A84C 0%, rgba(201,168,76,0.2) 100%)' }} />
                <div className="px-4 pt-3 pb-3 flex gap-3 items-start">
                  {/* Photo — clickable */}
                  <button
                    onClick={() => setPhotoModal(true)}
                    className="flex-shrink-0 relative group"
                    title="Click to enlarge photo"
                  >
                    {person.profile_photo ? (
                      <img src={person.profile_photo} alt="" className="w-16 h-16 rounded-full object-cover" style={{ border: '3px solid #E5B87A', boxShadow: '0 2px 10px rgba(0,0,0,0.12)' }} />
                    ) : (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center text-base font-bold text-white" style={{ background: person.avatar_color || '#A1723D', border: '3px solid rgba(229,184,122,0.5)', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                        {initials(`${person.first_name} ${person.last_name}`)}
                      </div>
                    )}
                    <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="18" height="18"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </div>
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-extrabold text-navy leading-tight">
                          {person.first_name} {person.last_name}
                          {person.preferred_name && person.preferred_name !== person.first_name && (
                            <span className="ml-1.5 text-[9px] text-gray-400 font-normal">"{person.preferred_name}"</span>
                          )}
                        </div>
                        {person.designation && (
                          <div className="text-[10px] font-semibold text-gray-600 mt-0.5">{person.designation}</div>
                        )}
                      </div>
                      {canManagePeople && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => setShowEdit(true)} className="px-2 py-1 text-[9px] font-semibold text-gold border border-gold/40 rounded hover:bg-gold/10">✏ Edit</button>
                          <button
                            onClick={archivePerson}
                            disabled={archiving}
                            className={`px-2 py-1 text-[9px] font-semibold rounded border transition-all ${person?.employee_status === 'archived' ? 'text-emerald-600 border-emerald-400/50 hover:bg-emerald-50' : 'text-amber-600 border-amber-400/50 hover:bg-amber-50'}`}
                          >{archiving ? '…' : person?.employee_status === 'archived' ? '↩ Restore' : '📦 Archive'}</button>
                          {/* Permanent deletion clears every reference to somebody
                              across the database and cannot be undone, so it stays
                              with administrators however the page was granted. The
                              API says the same. */}
                          {person?.employee_status === 'archived' && parseRoles(currentUser?.user_role).includes('admin') && (
                            <button onClick={deletePerson} className="px-2 py-1 text-[9px] font-semibold rounded border text-red-600 border-red-300 hover:bg-red-50">🗑 Delete</button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className="text-[8px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{person.employee_id}</span>
                      {person.employment_type && (
                        <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full ${
                          person.employment_type === 'contractor' ? 'bg-amber-100 text-amber-700'
                          : person.employment_type === 'fixed_term' ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-50 text-blue-600'
                        }`}>{EMPLOYMENT_TYPE_LABELS[person.employment_type] || person.employment_type}</span>
                      )}
                      {!!person.sub_contractor && (
                        <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200"
                          title="Engaged through another supplier — excluded from the org chart, hours overview and adoption views">Sub-contractor</span>
                      )}
                      {person.employee_status && (
                        <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full ${
                          person.employee_status === 'active'   ? 'bg-emerald-50 text-emerald-700'
                          : person.employee_status === 'on_leave' ? 'bg-amber-50 text-amber-700'
                          : person.employee_status === 'archived' ? 'bg-gray-100 text-gray-500'
                          : 'bg-red-50 text-red-600'
                        } capitalize`}>{person.employee_status.replace(/_/g, ' ')}</span>
                      )}
                      {person.team_name && (
                        <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-navy/10 text-navy">{person.team_name}</span>
                      )}
                      {person.security_clearance && person.security_clearance !== 'none' && (
                        <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700">{person.security_clearance.toUpperCase()}</span>
                      )}
                    </div>

                    {/* Quick contact */}
                    {(person.email_work || person.work_phone) && (
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {person.email_work && (
                          <a href={`mailto:${person.email_work}`} className="text-[8px] text-gray-500 hover:text-gold transition-colors flex items-center gap-1">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                            {person.email_work}
                          </a>
                        )}
                        {person.work_phone && (
                          <a href={`tel:${person.work_phone}`} className="text-[8px] text-gray-500 hover:text-gold transition-colors flex items-center gap-1">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.81 19.79 19.79 0 01.12 2.18 2 2 0 012.11 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.46-.46a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>
                            {person.work_phone}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Photo lightbox */}
              {photoModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] cursor-pointer" onClick={() => setPhotoModal(false)}>
                  <div className="relative" onClick={e => e.stopPropagation()}>
                    {person.profile_photo ? (
                      <img src={person.profile_photo} alt="" style={{ maxWidth: '80vw', maxHeight: '75vh', borderRadius: 20, objectFit: 'contain', border: '4px solid #C9A84C', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', display: 'block' }} />
                    ) : (
                      <div style={{ width: 220, height: 220, borderRadius: '50%', background: person.avatar_color || '#A1723D', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 72, fontWeight: 700, color: '#fff', border: '4px solid #C9A84C' }}>
                        {initials(`${person.first_name} ${person.last_name}`)}
                      </div>
                    )}
                    <div style={{ textAlign: 'center', marginTop: 14, color: '#fff' }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{person.first_name} {person.last_name}</div>
                      {person.designation && <div style={{ fontSize: 11, opacity: 0.55, marginTop: 3 }}>{person.designation}</div>}
                    </div>
                    <button onClick={() => setPhotoModal(false)} style={{ position: 'absolute', top: -14, right: -14, width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex border-b border-gray-200 overflow-x-auto">
                {/* Item 19. Work, contact and clearance carry pay band,
                    probation, home address and vetting detail — HR and admin
                    only. Everyone else sees the directory tabs. The API redacts
                    the same fields (redactPerson), so hiding the tab is the
                    visible half of a rule that is enforced server-side. */}
                {['details', ...(canManagePeople ? ['work','contact','clearance'] : []),
                  ...(canSeeLaptops ? ['laptop'] : []),
                  // No Leave tab for a contractor: there is no allowance to show and
                  // nothing to book against it.
                  ...(isContractor ? [] : ['leave']),
                  ...(canManagePeople ? ['documents','access','m365','ai usage'] : [])].map(t => (
                  <button
                    key={t}
                    className={`px-2 py-1.5 text-[9px] font-medium border-b-2 -mb-px capitalize whitespace-nowrap ${tab === t ? 'text-gold border-gold font-semibold' : 'text-gray-400 border-transparent'}`}
                    onClick={() => setTab(t)}
                  >{t}</button>
                ))}
              </div>

              <div className="p-3">
                {tab === 'details' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4">
                    {[
                      ['Employee ID', person.employee_id],
                      ['Preferred Name', person.preferred_name],
                      ['Email', person.email_work],
                      // "DOB, Gender, Marital Status still showing - not needed." The edit
                      // form stopped asking a contractor for them; this panel went on
                      // showing the labels regardless, so they still read as fields that
                      // ought to be filled in.
                      ...(isContractor ? [] : [
                        ['Date of Birth', formatDate(person.date_of_birth)],
                        ['Gender', person.gender],
                        ['Marital Status', person.marital_status],
                      ]),
                    ].map(([l, v]) => (
                      <div key={l}>
                        <div className="text-[7px] font-bold text-gray-500 uppercase">{l}</div>
                        <div className="text-[10px]">{v || '-'}</div>
                      </div>
                    ))}
                  </div>
                )}

                {tab === 'work' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4">
                      {[
                        ['Team', person.team_name],
                        ['Job Title', person.designation],
                        ['Grade', person.grade],
                        ['Employment Type', EMPLOYMENT_TYPE_LABELS[person.employment_type] || person.employment_type],
                        ['Status', person.employee_status],
                        ['Start of Employment', formatDate(person.start_of_employment)],
                        ['End of Employment', formatDate(person.end_of_employment)],
                        ['Reporting Manager', manager ? `${manager.first_name} ${manager.last_name}` : '-'],
                        ['Rate Band', person.rate_band],
                        ['Working Hours', person.working_hours],
                        ['Source of Hire', person.source_of_hire],
                        ['FTE', person.fte != null ? `${person.fte} (${Math.round(person.fte * 100)}%)` : '1.0 (100%)'],
                        ...(person.fte < 1 ? [['Working Days', person.working_days
                          ? person.working_days.split(',').map(d => d[0].toUpperCase() + d.slice(1)).join(', ')
                          : 'Not recorded']] : []),
                        ...(person.home_days ? [['Usual Home Days', person.home_days
                          .split(',').map(d => d[0].toUpperCase() + d.slice(1)).join(', ')]] : []),
                        // ── ONE FIGURE, THE CALCULATED ONE ──
                        //
                        // This showed people.holiday_entitlement — a typed full-time
                        // baseline — beside a different number on the Leave tab, so the
                        // screen contradicted itself: "Holiday entitlement says 0 days/yr,
                        // but under Leave it says 25 days available". Anna Webster's record
                        // held 0, which is why hers read zero and El's read correctly.
                        //
                        // It now shows the allowance actually calculated for the year,
                        // which IS what the Leave tab shows, because there is only one
                        // number to show. A contractor accrues none, so the row is absent
                        // rather than present and zero.
                        ...(isContractor ? [] : [[
                          'Annual Leave This Year',
                          annualAllowance != null
                            ? `${annualAllowance} days (calculated from FTE and start date)`
                            : 'not calculated yet',
                        ]]),
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div className="text-[7px] font-bold text-gray-500 uppercase">{l}</div>
                          <div className="text-[10px]">{v || '-'}</div>
                        </div>
                      ))}
                    </div>
                    {/* Probation section */}
                    <div className="border-t border-gray-100 pt-2">
                      <div className="text-[8px] font-bold text-gray-500 uppercase mb-1.5">Probation</div>
                      <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                        <div>
                          <div className="text-[7px] font-bold text-gray-500 uppercase">Probation End</div>
                          <div className="text-[10px]">{formatDate(person.probation_end) || '-'}</div>
                        </div>
                        <div>
                          <div className="text-[7px] font-bold text-gray-500 uppercase">Status</div>
                          <div className="text-[10px] capitalize">{(person.probation_status || 'not_started').replace(/_/g, ' ')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {tab === 'contact' && (
                  <div>
                    <h4 className="text-[9px] font-bold mb-2">Contact Details</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-2 gap-x-4 mb-3">
                      {[
                        ['Work Phone', person.work_phone],
                        ['Extension', person.extension],
                        ['Personal Mobile', person.personal_mobile],
                        ['Personal Email', person.personal_email],
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div className="text-[7px] font-bold text-gray-500 uppercase">{l}</div>
                          <div className="text-[10px]">{v || '-'}</div>
                        </div>
                      ))}
                    </div>
                    <h4 className="text-[9px] font-bold mb-2">Address</h4>
                    {/* home_address is the column the old My Info form wrote to. Nothing
                        writes it now, but anything already in it still has to be visible
                        or a person's address would look like it had been lost. */}
                    <div className="text-[10px] mb-3">
                      {[person.address_line1, person.address_line2, person.city, person.county, person.postcode]
                        .filter(Boolean).join(', ') || person.home_address || '-'}
                    </div>
                    <h4 className="text-[9px] font-bold mb-2">Emergency Contact</h4>
                    <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                      {[
                        ['Name', person.emergency_contact_name],
                        ['Phone', person.emergency_contact_phone],
                        ['Relationship', person.emergency_contact_relationship],
                        ['Address', person.emergency_contact_address],
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div className="text-[7px] font-bold text-gray-500 uppercase">{l}</div>
                          <div className="text-[10px]">{v || '-'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tab === 'clearance' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4">
                    {[
                      ['Level', (person.security_clearance || 'None').toUpperCase()],
                      ['Expiry', formatDate(person.clearance_expiry)],
                      ['RRSV Number', person.rrsv_number],
                      ['Pass Expiry', formatDate(person.pass_expiry)],
                      ['RDN Expiry', formatDate(person.rdn_expiry)],
                      ['Sponsor', person.clearance_sponsor],
                      ['Reference', person.clearance_reference],
                      ['Right to Work', person.right_to_work],
                      ['RTW Expiry', formatDate(person.rtw_expiry)],
                    ].map(([l, v]) => (
                      <div key={l}>
                        <div className="text-[7px] font-bold text-gray-500 uppercase">{l}</div>
                        <div className={`text-[10px] font-semibold ${l === 'Expiry' && clearanceExpiring ? 'text-amber-500' : ''}`}>
                          {v || '-'}{l === 'Expiry' && clearanceExpiring && ' ⚠ Expiring soon'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {tab === 'laptop' && canSeeLaptops && (
                  <PersonLaptopTab personId={person.id} onOpenLaptops={() => navigate('laptops', person.id)} />
                )}

                {tab === 'documents' && canManagePeople && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">HR Documents (M365)</div>
                      <button
                        onClick={() => setShowAddDoc(true)}
                        className="px-2 py-0.5 text-[8px] font-semibold bg-navy text-white rounded hover:bg-navy/80"
                      >+ Add Document</button>
                    </div>

                    {showAddDoc && (
                      <div className="bg-gold/5 border border-gold/20 rounded p-2 mb-3">
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">Document Type *</label>
                            <select className="form-select text-[10px]" value={docForm.doc_type} onChange={e => setDocForm(p => ({ ...p, doc_type: e.target.value }))}>
                              {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">Title *</label>
                            <input className="form-input text-[10px]" placeholder="e.g. Passport (expires 2029)" value={docForm.title} onChange={e => setDocForm(p => ({ ...p, title: e.target.value }))} />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">M365 / SharePoint URL *</label>
                            <input className="form-input text-[10px]" placeholder="https://..." value={docForm.url} onChange={e => setDocForm(p => ({ ...p, url: e.target.value }))} />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
                            <input className="form-input text-[10px]" placeholder="Optional notes..." value={docForm.notes} onChange={e => setDocForm(p => ({ ...p, notes: e.target.value }))} />
                          </div>
                        </div>
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => setShowAddDoc(false)} className="px-2 py-0.5 text-[8px] bg-gray-100 text-gray-600 rounded">Cancel</button>
                          <button onClick={addDoc} disabled={!docForm.title.trim() || !docForm.url.trim()} className="px-2 py-0.5 text-[8px] font-semibold bg-emerald-600 text-white rounded disabled:opacity-40">Save</button>
                        </div>
                      </div>
                    )}

                    {personDocs.length === 0 && !showAddDoc ? (
                      <div className="text-center py-6 text-[10px] text-gray-400">No documents linked yet.</div>
                    ) : (
                      <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Type</th>
                            <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Title</th>
                            <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Link</th>
                            <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Notes</th>
                            <th className="px-2 py-1" />
                          </tr>
                        </thead>
                        <tbody>
                          {personDocs.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-6 text-[9px] text-gray-400">No documents on record</td></tr>
                          )}
                          {personDocs.map(doc => (
                            <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-2 py-1">
                                <span className="text-[8px] font-semibold bg-navy/10 text-navy px-1.5 py-0.5 rounded capitalize">
                                  {DOC_TYPES.find(d => d.value === doc.doc_type)?.label || doc.doc_type}
                                </span>
                              </td>
                              <td className="px-2 py-1 text-[10px] font-medium">{doc.title}</td>
                              <td className="px-2 py-1">
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[9px] text-gold font-semibold hover:underline truncate block max-w-[120px]"
                                  title={doc.url}
                                >
                                  Open in M365 →
                                </a>
                              </td>
                              <td className="px-2 py-1 text-[9px] text-gray-400">{doc.notes || '-'}</td>
                              <td className="px-2 py-1 text-right">
                                <button
                                  onClick={() => deleteDoc(doc.id)}
                                  className="text-[8px] text-red-400 hover:text-red-600 font-semibold"
                                >Remove</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    )}
                  </div>
                )}

                {tab === 'access' && canManagePeople && (() => {
                  const isRealAdmin = parseRoles(currentUser?.user_role).includes('admin');
                  const isSelf = person?.id === currentUser?.id;
                  const roleLocked = !isRealAdmin || isSelf;
                  return (
                  <div>
                    <h4 className="text-[9px] font-bold mb-3 text-gray-600 uppercase tracking-wide">System Access Roles</h4>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-[9px] text-amber-700">
                      Changing someone's roles affects what they can see and do across the entire platform. At least one role must be assigned.
                    </div>
                    {!isRealAdmin && (
                      <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-3 text-[9px] text-gray-600">🔒 Only an administrator can change roles. This is read-only for you.</div>
                    )}
                    {isRealAdmin && isSelf && (
                      <div className="bg-red-50 border border-red-200 rounded p-2 mb-3 text-[9px] text-red-700">🔒 For security you cannot change your own role. Ask another administrator to make changes.</div>
                    )}
                    <div className="grid gap-1.5">
                      {/* Item 35. Thirteen checkboxes in no order anybody could
                          see: the list was written roughly by seniority, which
                          reads as arbitrary to whoever is looking for "Finance". */}
                      {sortByLabel(ROLES).map(role => {
                        const currentRoles = parseRoles(person.user_role);
                        const isChecked = currentRoles.includes(role.value);
                        return (
                          <label
                            key={role.value}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded border text-[10px] transition-all select-none ${
                              isChecked
                                ? 'bg-gold/10 border-gold text-gold font-semibold'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-gold/40 hover:bg-gold/5'
                            } ${(roleChanging || roleLocked) ? 'opacity-50 pointer-events-none cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={roleChanging || roleLocked}
                              onChange={() => toggleRole(person.id, person.user_role, role.value)}
                              className="accent-gold w-3 h-3 flex-shrink-0"
                            />
                            <span className="flex-1">{role.label}</span>
                            {isChecked && (
                              <span className="text-[8px] bg-navy text-white px-1.5 py-0.5 rounded">Active</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    {person.last_login && (
                      <div className="mt-3 text-[8px] text-gray-400">Last login: {person.last_login}</div>
                    )}

                    {/* ── Standby-specific permissions ─────────────────── */}
                    <h4 className="text-[9px] font-bold mt-4 mb-2 text-gray-600 uppercase tracking-wide">Standby &amp; Overtime</h4>
                    <div className="grid gap-1.5">
                      {/* Can Claim Standby */}
                      <label className={`flex items-center gap-2.5 px-3 py-2 rounded border text-[10px] cursor-pointer transition-all select-none ${
                        person.can_claim_standby !== 0
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold'
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      }`}>
                        <input
                          type="checkbox"
                          checked={person.can_claim_standby !== 0}
                          onChange={async () => {
                            const newVal = person.can_claim_standby !== 0 ? 0 : 1;
                            await api.put(`people/${person.id}`, { can_claim_standby: newVal });
                            setPeople(prev => prev.map(p => p.id === person.id ? { ...p, can_claim_standby: newVal } : p));
                          }}
                          className="accent-emerald-600 w-3 h-3 flex-shrink-0"
                        />
                        <span className="flex-1">Can claim standby overtime pay (£2/hr)</span>
                        {person.can_claim_standby === 0 && (
                          <span className="text-[8px] bg-gray-400 text-gray-950 px-1.5 py-0.5 rounded">£0 — non-claimable</span>
                        )}
                      </label>

                      {/* Can Claim Overtime */}
                      <label className={`flex items-center gap-2.5 px-3 py-2 rounded border text-[10px] cursor-pointer transition-all select-none ${
                        person.can_claim_overtime !== 0
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold'
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      }`}>
                        <input
                          type="checkbox"
                          checked={person.can_claim_overtime !== 0}
                          onChange={async () => {
                            const newVal = person.can_claim_overtime !== 0 ? 0 : 1;
                            await api.put(`people/${person.id}`, { can_claim_overtime: newVal });
                            setPeople(prev => prev.map(p => p.id === person.id ? { ...p, can_claim_overtime: newVal } : p));
                          }}
                          className="accent-emerald-600 w-3 h-3 flex-shrink-0"
                        />
                        <span className="flex-1">Can claim overtime (hours logged over the daily cap)</span>
                        {person.can_claim_overtime === 0 && (
                          <span className="text-[8px] bg-gray-400 text-gray-950 px-1.5 py-0.5 rounded">Not eligible</span>
                        )}
                      </label>
                    </div>
                    <div className="mt-2 text-[8px] text-gray-400">
                      Non-claimable personnel appear on the rota but no overtime value is generated for their standby slots.
                    </div>

                    <McpAccessPanel personId={person.id} />
                    <EndSessionsPanel person={person} />
                  </div>
                  );
                })()}

                {tab === 'm365' && canManagePeople && (
                  <M365PersonTab personId={person.id} />
                )}

                {tab === 'ai usage' && canManagePeople && (
                  <AiUsagePersonTab personId={person.id} />
                )}

                {tab === 'leave' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[9px] font-bold text-gray-500 uppercase">2026–27 Allowances</div>
                      {/* No "+ Add Allowance": "this shouldn't be there". Every row here is
                          calculated — the annual entitlement from FTE and the start date,
                          the long-service day on the anniversary, the carry-over on 1 March
                          — so a typed one could only disagree with the arithmetic. */}
                      <div className="text-[8px] text-gray-400">calculated automatically</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                      {leaveAllowances.map(la => (
                        <div key={la.id} className="bg-gray-50 border border-gray-200 rounded p-2">
                          <div className="text-[8px] font-semibold capitalize">{la.leave_type.replace(/_/g, ' ')}</div>
                          <div className="text-[11px] font-extrabold text-navy mt-0.5">{la.total_days}d</div>
                          <div className="flex justify-between mt-1">
                            {allowanceLeft(la) !== null && <span className="text-[9px] text-emerald-600 font-bold">{allowanceLeft(la)}d avail</span>}
                            <span className="text-[9px] text-gray-400">{la.booked_days}d used</span>
                          </div>
                        </div>
                      ))}
                      {leaveAllowances.length === 0 && (
                        <div className="col-span-3 text-center py-4 text-[9px] text-gray-400">
                          No leave allowances for 2026-27 yet. They are opened automatically — check the start date and
                          FTE on the Work tab are filled in.
                        </div>
                      )}
                    </div>

                    {/* Leave requests breakdown */}
                    <div className="border-t border-gray-100 pt-3">
                      <div className="text-[9px] font-bold text-gray-500 uppercase mb-2">Holiday Requests</div>
                      {leaveRequests.length === 0 ? (
                        <div className="text-center py-3 text-[9px] text-gray-400">No leave requests on record</div>
                      ) : (
                        <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Type</th>
                              <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Dates</th>
                              <th className="px-2 py-1 text-center text-[7px] uppercase text-gray-500 font-bold">Days</th>
                              <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Status</th>
                              <th className="px-2 py-1 text-left text-[7px] uppercase text-gray-500 font-bold">Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveRequests.map(lr => {
                              const statusConfig = {
                                approved:  { cls: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
                                pending:   { cls: 'bg-amber-100 text-amber-700',     label: 'Pending'  },
                                rejected:  { cls: 'bg-red-100 text-red-600',         label: 'Rejected' },
                                cancelled: { cls: 'bg-gray-100 text-gray-400',       label: 'Cancelled'},
                              }[lr.status] || { cls: 'bg-gray-100 text-gray-500', label: lr.status };
                              return (
                                <tr key={lr.id} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="px-2 py-1 text-[9px] capitalize">{(lr.leave_type || '').replace(/_/g, ' ')}</td>
                                  <td className="px-2 py-1 text-[9px] whitespace-nowrap">
                                    {formatDate(lr.start_date)}
                                    {lr.start_date !== lr.end_date && <> → {formatDate(lr.end_date)}</>}
                                  </td>
                                  <td className="px-2 py-1 text-[9px] text-center font-semibold">{lr.days}d</td>
                                  <td className="px-2 py-1">
                                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${statusConfig.cls}`}>
                                      {statusConfig.label}
                                    </span>
                                  </td>
                                  <td className="px-2 py-1 text-[8px] text-gray-400">
                                    {lr.rejection_reason || lr.reason || ''}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="bg-gray-50 border-t border-gray-200">
                              <td colSpan="2" className="px-2 py-1 text-[8px] font-bold text-gray-500 text-right">
                                Approved total, excluding sick:
                              </td>
                              <td className="px-2 py-1 text-[9px] font-extrabold text-emerald-600 text-center">
                                {approvedLeaveDays(leaveRequests)}d
                              </td>
                              <td colSpan="2" />
                            </tr>
                          </tfoot>
                        </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded flex items-center justify-center min-h-[200px] text-[10px] text-gray-400">
              Select a person
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <Modal title="Add Person" onClose={() => setShowCreate(false)} wide footer={
          <>
            <button onClick={() => setShowCreate(false)} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
            <button onClick={() => {
              const f = document.getElementById('ppl-form');
              // Rate Band is deliberately not here: it belongs with the rest of
              // the commercial detail, set when the person is fully configured,
              // not on the four-field screen that creates the record.
              create({ first_name: f.fn.value, last_name: f.ln.value, email_work: f.em.value, designation: f.des.value, team_id: f.tm.value, employment_type: f.et.value, security_clearance: f.sc.value, start_of_employment: f.sd.value });
            }} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Add Person</button>
          </>
        }>
          <form id="ppl-form">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">First Name *</label><input name="fn" className="form-input" /></div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Last Name *</label><input name="ln" className="form-input" /></div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Work Email *</label><input name="em" className="form-input" type="email" /></div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Job Title</label>
                <select name="des" className="form-select">
                  <option value="">Select...</option>
                  {jobTitles.map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                </select>
                {jobTitles.length === 0 && (
                  <p className="mt-0.5 text-[7px] text-amber-700">No job titles yet — add them in Admin → Settings → Job Titles.</p>
                )}
              </div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Team</label>
                <select name="tm" className="form-select"><option value="">Select...</option>{sortedTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
              </div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Employment Type</label>
                <select name="et" className="form-select" defaultValue="permanent"><option value="permanent">Permanent</option><option value="fixed_term">Fixed Term</option><option value="contractor">Contractor</option></select>
              </div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Clearance</label>
                <select name="sc" className="form-select">
                  {clearances.length
                    ? clearances.map(o => <option key={o.id} value={o.value}>{o.label}</option>)
                    : <option value="none">None</option>}
                </select>
              </div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Start Date</label><input name="sd" className="form-input" type="date" /></div>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEdit && person && (
        <EditPersonModal
          person={person}
          people={people}
          teams={sortedTeams}
          jobTitles={jobTitles}
          grades={grades}
          clearances={clearances}
          companyLeaveDays={companyLeaveDays}
          onClose={() => setShowEdit(false)}
          onSave={savePerson}
        />
      )}

    </div>
  );
}

// The weekdays a part-timer can be recorded as working, as stored on the person.
const WORK_WEEKDAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri']];

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{label}</label>
      {children}
    </div>
  );
}

function EditPersonModal({ person, people, teams, jobTitles = [], grades = [], clearances = [], companyLeaveDays = null, onClose, onSave }) {
  const [form, setForm] = useState({
    first_name:                    person.first_name || '',
    last_name:                     person.last_name || '',
    preferred_name:                person.preferred_name || '',
    email_work:                    person.email_work || '',
    date_of_birth:                 person.date_of_birth || '',
    gender:                        person.gender || '',
    marital_status:                person.marital_status || '',
    // Work
    team_id:                       person.team_id || '',
    designation:                   person.designation || '',
    grade:                         person.grade || '',
    employment_type:               person.employment_type || 'permanent',
    employee_status:               person.employee_status || 'active',
    start_of_employment:           person.start_of_employment || '',
    end_of_employment:             person.end_of_employment || '',
    date_of_joining:               person.date_of_joining || '',
    reporting_manager_id:          person.reporting_manager_id || '',
    rate_band:                     person.rate_band || '',
    working_hours:                 person.working_hours || '',
    source_of_hire:                person.source_of_hire || '',
    fte:                           person.fte != null ? person.fte : 1.0,
    working_days:                  person.working_days || '',
    home_days:                     person.home_days || '',
    holiday_entitlement:           person.holiday_entitlement != null ? person.holiday_entitlement : 25.0,
    expected_weekly_hours:         person.expected_weekly_hours != null ? person.expected_weekly_hours : 37.5,
    // Probation
    probation_end:                 person.probation_end || '',
    probation_status:              person.probation_status || 'not_started',
    // Contact
    work_phone:                    person.work_phone || '',
    extension:                     person.extension || '',
    personal_mobile:               person.personal_mobile || '',
    personal_email:                person.personal_email || '',
    address_line1:                 person.address_line1 || '',
    address_line2:                 person.address_line2 || '',
    city:                          person.city || '',
    county:                        person.county || '',
    postcode:                      person.postcode || '',
    emergency_contact_name:        person.emergency_contact_name || '',
    emergency_contact_phone:       person.emergency_contact_phone || '',
    emergency_contact_relationship: person.emergency_contact_relationship || '',
    emergency_contact_address:     person.emergency_contact_address || '',
    // Clearance
    security_clearance:            person.security_clearance || 'none',
    rrsv_number:                   person.rrsv_number || '',
    pass_expiry:                   person.pass_expiry || '',
    rdn_expiry:                    person.rdn_expiry || '',
    clearance_expiry:              person.clearance_expiry || '',
    clearance_sponsor:             person.clearance_sponsor || '',
    clearance_reference:           person.clearance_reference || '',
    right_to_work:                 person.right_to_work || '',
    rtw_expiry:                    person.rtw_expiry || '',
    // Misc
    qms_exempt:                    person.qms_exempt ? 1 : 0,
    sub_contractor:                person.sub_contractor ? 1 : 0,
  });
  const [tab, setTab] = useState('details');
  const [saving,      setSaving]      = useState(false);
  const [activeBands, setActiveBands] = useState([]); // bands from active rate card
  // Item 11, option B: the same human being's external-resource record. Loaded
  // for contractors only, because that is the only case where the two records
  // both exist.
  const [resourceWorkers, setResourceWorkers] = useState([]);
  const [linking, setLinking] = useState(false);
  const loadResourceWorkers = () =>
    api.get('resource-workers/active').then(r => setResourceWorkers(Array.isArray(r) ? r : [])).catch(() => {});
  useEffect(() => {
    if (person.employment_type === 'contractor') loadResourceWorkers();
  }, [person.employment_type]);
  const linkedResource = resourceWorkers.find(r => r.person_id === person.id) || null;
  const linkResource = async (resourceId) => {
    setLinking(true);
    try {
      // Unlink the previous one first: the link is one-to-one, and leaving the old
      // row pointing here would break that quietly.
      if (linkedResource && linkedResource.id !== resourceId) {
        await api.put(`resource-workers/${linkedResource.id}`, { person_id: null });
      }
      if (resourceId) await api.put(`resource-workers/${resourceId}`, { person_id: person.id });
      await loadResourceWorkers();
    } catch (e) {
      alert(e.message || 'Could not link that record.');
    } finally {
      setLinking(false);
    }
  };

  // Load active rate card bands once for the cost hint
  useEffect(() => {
    api.get('rate-cards/active-bands').then(setActiveBands).catch(() => {});
  }, []);

  // Lookup day rate for the currently-selected band
  const selectedBandRate = activeBands.find(b => b.band_code === form.rate_band);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        ...form,
        team_id: form.team_id || null,
        reporting_manager_id: form.reporting_manager_id || null,
        // Only a part-timer has a pattern; going full time clears it.
        working_days: form.fte < 1 ? form.working_days : null,
        home_days: form.home_days || null,
      });
    } finally {
      setSaving(false);
    }
  };

  // People available as managers (exclude self)
  const managerOptions = people.filter(p => p.id !== person.id);

  // ── A CONTRACTOR'S RECORD IS SHORTER (El's item 11) ────────────────────────
  //
  // "Contractors should only need — name, team, job title, start date, work
  // email, clearance. No other details needed."
  //
  // They are not on the payroll, so date of birth, marital status, rate band,
  // FTE, holiday entitlement, expected weekly hours, probation, the home address
  // and the emergency contacts are either meaningless for them or somebody else's
  // record to keep. Asking for them implies they ought to be filled in, and a
  // form full of fields nobody completes makes a missing answer indistinguishable
  // from an irrelevant one.
  //
  // HIDDEN, NOT CLEARED. The form holds every field it loaded and saves them all
  // back, so anything already recorded against a contractor survives — it is
  // simply not asked for again. The trim deletes nothing.
  //
  // Two things are kept beyond El's six: employee status, which is the only way a
  // contractor gets deactivated, and the link to their external-resource record,
  // which is the other half of this same item.
  const isContractorPerson = form.employment_type === 'contractor';
  const TABS = isContractorPerson
    ? ['details','work','clearance']
    : ['details','work','contact','clearance'];

  return (
    <Modal
      title={`Edit — ${person.first_name} ${person.last_name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }
    >
      {/* Sub-tabs */}
      <div className="flex border-b border-gray-200 mb-3 -mt-1">
        {TABS.map(t => (
          <button
            key={t}
            className={`px-3 py-1.5 text-[9px] font-medium border-b-2 -mb-px capitalize ${tab === t ? 'text-gold border-gold font-semibold' : 'text-gray-400 border-transparent'}`}
            onClick={() => setTab(t)}
          >{t}</button>
        ))}
      </div>

      {tab === 'details' && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="First Name *"><input className="form-input" value={form.first_name} onChange={e => set('first_name', e.target.value)} /></Field>
          <Field label="Last Name *"><input className="form-input" value={form.last_name} onChange={e => set('last_name', e.target.value)} /></Field>
          <Field label="Preferred Name"><input className="form-input" value={form.preferred_name} onChange={e => set('preferred_name', e.target.value)} /></Field>
          <Field label="Work Email"><input className="form-input" type="email" value={form.email_work} onChange={e => set('email_work', e.target.value)} /></Field>
          {/* Payroll and HR facts. Not asked of a contractor (item 11). */}
          {!isContractorPerson && <>
            <Field label="Date of Birth"><input className="form-input" type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} /></Field>
            <Field label="Gender">
              <select className="form-select" value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="">Not specified</option>
                <option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
              </select>
            </Field>
            <Field label="Marital Status">
              <select className="form-select" value={form.marital_status} onChange={e => set('marital_status', e.target.value)}>
                <option value="">Not specified</option>
                <option>Single</option><option>Married</option><option>Civil Partnership</option><option>Co-habiting</option><option>Divorced</option><option>Widowed</option>
              </select>
            </Field>
          </>}
          <Field label="Avatar Colour">
            <div className="flex items-center gap-2 mt-1">
              <span className="w-4 h-4 rounded-full border border-gray-200" style={{ background: person.avatar_color || '#A1723D' }} />
              <span className="text-[9px] text-gray-500">
                {form.employment_type === 'contractor'
                  ? 'Contractors share one colour.'
                  : 'Taken from the team. Change it in Admin → Settings → Teams.'}
              </span>
            </div>
          </Field>
        </div>
      )}

      {tab === 'work' && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Job Title">
            <select className="form-select" value={form.designation} onChange={e => set('designation', e.target.value)}>
              <option value="">Not set</option>
              {jobTitles.map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
              {/* A title recorded before the list existed still shows, rather
                  than the field silently reading as empty. */}
              {form.designation && !jobTitles.some(o => o.value === form.designation) && (
                <option value={form.designation}>{form.designation} (not on the list)</option>
              )}
            </select>
          </Field>
          <Field label="Team">
            <select className="form-select" value={form.team_id} onChange={e => set('team_id', e.target.value)}>
              <option value="">No team</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          {!isContractorPerson && (
            <Field label="Grade">
              <select className="form-select" value={form.grade} onChange={e => set('grade', e.target.value)}>
                <option value="">Not set</option>
                {grades.map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
                {form.grade && !grades.some(o => o.value === form.grade) && (
                  <option value={form.grade}>{form.grade} (not on the list)</option>
                )}
              </select>
            </Field>
          )}
          <Field label={isContractorPerson ? 'Start Date' : 'Start of Employment'}>
            <input className="form-input" type="date" value={form.start_of_employment} onChange={e => set('start_of_employment', e.target.value)} />
          </Field>
          {!isContractorPerson && (
            <Field label="End of Employment"><input className="form-input" type="date" value={form.end_of_employment} onChange={e => set('end_of_employment', e.target.value)} /></Field>
          )}
          <Field label="Employment Type">
            <select className="form-select" value={form.employment_type} onChange={e => set('employment_type', e.target.value)}>
              {EMPLOYMENT_TYPES.map(et => <option key={et} value={et}>{EMPLOYMENT_TYPE_LABELS[et] || et}</option>)}
            </select>
          </Field>
          <Field label="Employee Status">
            <select className="form-select" value={form.employee_status} onChange={e => set('employee_status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on_leave">On Leave</option>
              <option value="terminated">Terminated</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
          {/* A refinement of 'contractor': engaged through another supplier,
              not part of day-to-day operations. The server clears it if the
              employment type stops being contractor. */}
          {isContractorPerson && (
          <Field label="Sub-contractor">
            <label className="flex items-center gap-2 cursor-pointer select-none mt-1">
              <input
                type="checkbox"
                checked={!!form.sub_contractor}
                onChange={e => set('sub_contractor', e.target.checked ? 1 : 0)}
                className="w-3.5 h-3.5 accent-navy"
              />
              <span className="text-[11px] text-gray-600">Engaged through another supplier — hidden from the org chart, hours overview and adoption views</span>
            </label>
          </Field>
          )}
          {/* ITEM 11, OPTION B: the two records, tied together.
              A contractor can exist as this staff record AND as an external
              resource with a portal login, contracts, timesheets and invoices,
              and until now nothing said they were the same person — so one could
              be archived while the other stayed active, and their clearance and
              their day rate sat in two records nobody could reconcile.
              Both records stay, which is right: they exist for different reasons.
              This is the link. */}
          {isContractorPerson && (
            <div className="col-span-2 border-t border-gray-100 pt-2">
              <div className="text-[9px] font-bold text-gray-600 uppercase mb-1">External Resource Record</div>
              <select className="form-select" disabled={linking}
                value={linkedResource?.id || ''}
                onChange={e => linkResource(e.target.value)}>
                <option value="">Not linked</option>
                {resourceWorkers
                  // The free ones, plus whichever is already linked to this person.
                  .filter(r => !r.person_id || r.person_id === person.id)
                  .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'en-GB', { sensitivity: 'base' }))
                  .map(r => (
                    <option key={r.id} value={r.id}>
                      {r.first_name} {r.last_name}{r.company_name ? ` — ${r.company_name}` : ''}{r.email ? ` (${r.email})` : ''}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-[8px] text-gray-400">
                {linkedResource
                  ? 'Linked. Their contracts, timesheets and invoices are on the Contractor Dashboard; their clearance and team are here.'
                  : 'Contractors also exist on the Contractor Dashboard, where their contracts, timesheets and invoices live. Link the two so it is clear they are the same person.'}
              </p>
              {linkedResource && (
                <a href={`#contractor-dashboard/${linkedResource.id}`} className="text-[8px] text-navy font-semibold hover:underline">
                  Open on the Contractor Dashboard →
                </a>
              )}
            </div>
          )}

          {/* Payroll, contractual and HR detail. Item 11: a contractor has none of
              it — they invoice for days worked, accrue no holiday, sit on no
              rate band and serve no probation. Hidden rather than cleared, so
              anything already recorded survives. */}
          {!isContractorPerson && <>
          <Field label="QMS Exempt">
            <label className="flex items-center gap-2 cursor-pointer select-none mt-1">
              <input
                type="checkbox"
                checked={!!form.qms_exempt}
                onChange={e => set('qms_exempt', e.target.checked ? 1 : 0)}
                className="w-3.5 h-3.5 accent-navy"
              />
              <span className="text-[11px] text-gray-600">Exempt from document acknowledgement requirements</span>
            </label>
          </Field>
          <Field label="Reporting Manager">
            <select className="form-select" value={form.reporting_manager_id} onChange={e => set('reporting_manager_id', e.target.value)}>
              <option value="">No manager</option>
              {managerOptions.map(p => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name} {p.designation ? `— ${p.designation}` : ''}</option>
              ))}
            </select>
          </Field>
          <Field label="Rate Band">
            <select className="form-select" value={form.rate_band} onChange={e => set('rate_band', e.target.value)}>
              <option value="">None</option>
              {/* Prefer active rate card bands; fall back to hardcoded list */}
              {activeBands.length > 0
                ? activeBands.map(b => <option key={b.band_code} value={b.band_code}>{b.band_code}</option>)
                : RATE_BANDS.map(b => <option key={b}>{b}</option>)
              }
            </select>
            {selectedBandRate ? (
              <p className="mt-1 text-[9px] text-gray-500 flex items-center gap-1">
                <span className="font-mono text-[8px] bg-navy/10 text-navy px-1 rounded">{selectedBandRate.band_code}</span>
                <span className="text-gray-400">{selectedBandRate.band_name}</span>
                {/* The sell rate comes only to holders of Rate Cards, sow:edit or sow:see-financials. */}
                {selectedBandRate.day_rate != null && (
                <span className="ml-auto font-bold text-navy">£{Number(selectedBandRate.day_rate).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<span className="font-normal text-gray-400">/day</span></span>
                )}
              </p>
            ) : form.rate_band && activeBands.length > 0 ? (
              <p className="mt-1 text-[9px] text-amber-600">Band not found in current rate card</p>
            ) : null}
          </Field>
          <Field label="Source of Hire"><input className="form-input" value={form.source_of_hire} onChange={e => set('source_of_hire', e.target.value)} /></Field>
          <Field label="Working Hours"><input className="form-input" value={form.working_hours} placeholder="e.g. 37.5" onChange={e => set('working_hours', e.target.value)} /></Field>
          <Field label="FTE (Full-Time Equivalent)">
            <select className="form-select" value={String(form.fte)} onChange={e => set('fte', parseFloat(e.target.value))}>
              <option value="1">1.0 — Full-time (5 days)</option>
              <option value="0.8">0.8 — 4 days/week</option>
              <option value="0.6">0.6 — 3 days/week</option>
              <option value="0.5">0.5 — Half-time</option>
              <option value="0.4">0.4 — 2 days/week</option>
              <option value="0.2">0.2 — 1 day/week</option>
            </select>
          </Field>
          {/* ── WHICH DAYS A PART-TIMER WORKS ──
              Their leave is charged against these, a whole day each, so Wednesday
              to Friday costs three days for somebody who works all three. Left
              blank, leave is costed at the FTE average across every weekday. */}
          {form.fte < 1 && (() => {
            const ticked = form.working_days ? form.working_days.split(',') : [];
            const toggle = d => set('working_days', WORK_WEEKDAYS
              .map(([k]) => k).filter(k => (k === d) !== ticked.includes(k)).join(','));
            const expected = form.fte * 5;
            return (
              <Field label="Working Days">
                <div className="flex gap-1">
                  {WORK_WEEKDAYS.map(([k, label]) => (
                    <button key={k} type="button" onClick={() => toggle(k)} aria-pressed={ticked.includes(k)}
                      className={`px-2 py-1 text-[10px] font-semibold rounded border ${ticked.includes(k)
                        ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className={`mt-0.5 text-[8px] ${ticked.length && Number.isInteger(expected) && ticked.length !== expected ? 'text-amber-600' : 'text-gray-400'}`}>
                  {!ticked.length
                    ? 'Not recorded, so leave is costed at the FTE average. Tick the days they work to charge exactly those.'
                    : Number.isInteger(expected) && ticked.length !== expected
                      ? `${ticked.length} day${ticked.length === 1 ? '' : 's'} ticked, but ${form.fte} FTE is ${expected} a week.`
                      : 'Leave is charged against these days only.'}
                </p>
              </Field>
            );
          })()}
          {/* ── THE DAY THEY WORK FROM HOME EVERY WEEK ──
              They can set it themselves from the Bookings page; this is the same
              field. It shows as home on the planner and opens a reserved desk to
              others, a week at a time. */}
          {(() => {
            const ticked = form.home_days ? form.home_days.split(',') : [];
            const toggle = d => set('home_days', WORK_WEEKDAYS
              .map(([k]) => k).filter(k => (k === d) !== ticked.includes(k)).join(','));
            return (
              <Field label="Usual Home Days">
                <div className="flex gap-1">
                  {WORK_WEEKDAYS.map(([k, label]) => (
                    <button key={k} type="button" onClick={() => toggle(k)} aria-pressed={ticked.includes(k)}
                      className={`px-2 py-1 text-[10px] font-semibold rounded border ${ticked.includes(k)
                        ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <p className="mt-0.5 text-[8px] text-gray-400">
                  {ticked.length
                    ? 'Shown as working from home on these days, and a reserved desk opens to others on them a week ahead.'
                    : 'None. Tick a day they work from home every week.'}
                </p>
              </Field>
            );
          })()}
          {/* ── NOT TYPED ANY MORE ──
              "Still a manual option to add holiday entitlement in Edit but I want the
              system to calculate this."
              The company figure lives in Admin → Settings; here it is only stated, so it
              is clear what the allowance will be and where the number comes from. FTE and
              the start date above are the two things that change it. */}
          <Field label="Annual Leave">
            <div className="text-[10px] font-semibold text-navy pt-1">
              {form.employment_type === 'contractor'
                ? 'None — contractors invoice for days worked'
                : companyLeaveDays == null
                  ? 'Calculated automatically'
                  : `${Math.round(form.fte * companyLeaveDays * 2) / 2} days a year`}
            </div>
            <p className="mt-0.5 text-[8px] text-gray-400">
              {form.employment_type === 'contractor'
                ? 'Contractors accrue no holiday.'
                : `Worked out from the company entitlement (${companyLeaveDays ?? '—'} days full time), this person's FTE and their `
                  + 'start date, and adjusted for long service on each work anniversary. Change the company figure in '
                  + 'Admin → Settings → Annual Leave.'}
            </p>
          </Field>
          <Field label="Expected Weekly Hours">
            <input className="form-input" type="number" step="0.5" min="0" max="80" value={form.expected_weekly_hours} onChange={e => set('expected_weekly_hours', parseFloat(e.target.value) || 37.5)} />
            <p className="mt-0.5 text-[8px] text-gray-400">Used for the hours overview and Friday reminder emails (default 37.5h)</p>
          </Field>
          {/* Probation section */}
          <div className="col-span-2 border-t border-gray-100 pt-2">
            <div className="text-[9px] font-bold text-gray-600 uppercase mb-2">Probation</div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Probation End Date"><input className="form-input" type="date" value={form.probation_end} onChange={e => set('probation_end', e.target.value)} /></Field>
              <Field label="Probation Status">
                <select className="form-select" value={form.probation_status} onChange={e => set('probation_status', e.target.value)}>
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="passed">Passed</option>
                  <option value="extended">Extended</option>
                  <option value="failed">Failed</option>
                </select>
              </Field>
            </div>
          </div>
          </>}
        </div>
      )}

      {tab === 'contact' && (
        <div className="space-y-3">
          <div>
            <div className="text-[9px] font-bold text-gray-600 uppercase mb-2">Work Contact</div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Work Phone"><input className="form-input" value={form.work_phone} onChange={e => set('work_phone', e.target.value)} /></Field>
              <Field label="Extension"><input className="form-input" value={form.extension} onChange={e => set('extension', e.target.value)} /></Field>
              <Field label="Personal Mobile"><input className="form-input" value={form.personal_mobile} onChange={e => set('personal_mobile', e.target.value)} /></Field>
              <Field label="Personal Email"><input className="form-input" type="email" value={form.personal_email} onChange={e => set('personal_email', e.target.value)} /></Field>
            </div>
          </div>
          <div>
            <div className="text-[9px] font-bold text-gray-600 uppercase mb-2">Address</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><Field label="Address Line 1"><input className="form-input" value={form.address_line1} onChange={e => set('address_line1', e.target.value)} /></Field></div>
              <div className="col-span-2"><Field label="Address Line 2"><input className="form-input" value={form.address_line2} onChange={e => set('address_line2', e.target.value)} /></Field></div>
              <Field label="City"><input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} /></Field>
              <Field label="County"><input className="form-input" value={form.county} onChange={e => set('county', e.target.value)} /></Field>
              <Field label="Postcode"><input className="form-input" value={form.postcode} onChange={e => set('postcode', e.target.value)} /></Field>
            </div>
          </div>
          <div>
            <div className="text-[9px] font-bold text-gray-600 uppercase mb-2">Emergency Contact</div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name"><input className="form-input" value={form.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} /></Field>
              <Field label="Phone"><input className="form-input" value={form.emergency_contact_phone} onChange={e => set('emergency_contact_phone', e.target.value)} /></Field>
              <Field label="Relationship"><input className="form-input" value={form.emergency_contact_relationship} onChange={e => set('emergency_contact_relationship', e.target.value)} /></Field>
              <div className="col-span-2"><Field label="Address"><input className="form-input" value={form.emergency_contact_address} onChange={e => set('emergency_contact_address', e.target.value)} /></Field></div>
            </div>
          </div>
        </div>
      )}

      {tab === 'clearance' && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Security Clearance Level">
            <select className="form-select" value={form.security_clearance} onChange={e => set('security_clearance', e.target.value)}>
              {clearances.length
                ? clearances.map(o => <option key={o.id} value={o.value}>{o.label}</option>)
                : CLEARANCE_LEVELS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
          </Field>
          <Field label="Clearance Expiry"><input className="form-input" type="date" value={form.clearance_expiry} onChange={e => set('clearance_expiry', e.target.value)} /></Field>
          <Field label="Sponsor"><input className="form-input" value={form.clearance_sponsor} onChange={e => set('clearance_sponsor', e.target.value)} /></Field>
          <Field label="Reference"><input className="form-input" value={form.clearance_reference} onChange={e => set('clearance_reference', e.target.value)} /></Field>
          {/* Items 16 and 17. Both dates drive the Project Office reminder
              three weeks out; the RRSV number is an external reference. */}
          <Field label="RRSV Number"><input className="form-input" value={form.rrsv_number} onChange={e => set('rrsv_number', e.target.value)} /></Field>
          <Field label="Pass Expiry"><input className="form-input" type="date" value={form.pass_expiry} onChange={e => set('pass_expiry', e.target.value)} /></Field>
          <Field label="RDN Expiry"><input className="form-input" type="date" value={form.rdn_expiry} onChange={e => set('rdn_expiry', e.target.value)} /></Field>
          <Field label="Right to Work">
            <select className="form-select" value={form.right_to_work} onChange={e => set('right_to_work', e.target.value)}>
              <option value="">Not recorded</option>
              <option value="UK Citizen">UK Citizen</option>
              <option value="ILR">ILR</option>
              <option value="Visa">Visa</option>
              <option value="EU Settled">EU Settled</option>
              <option value="EU Pre-Settled">EU Pre-Settled</option>
            </select>
          </Field>
          <Field label="RTW Expiry"><input className="form-input" type="date" value={form.rtw_expiry} onChange={e => set('rtw_expiry', e.target.value)} /></Field>
        </div>
      )}
    </Modal>
  );
}
